import type { ToolConfig } from "../types";
import { createCompoundInterestTool, createEMITool, createPercentageTool } from "../factories";

export const nriTools: ToolConfig[] = [
  // 1. NRE FD Calculator
  createCompoundInterestTool({
    slug: "nre-fd-calculator",
    category: "nri",
    title: "NRE Fixed Deposit Calculator",
    shortTitle: "NRE FD Returns",
    description: "Maximize NRE FD returns with tax-free compound growth (6-7.5% p.a.)",
    keywords: ["NRE FD", "fixed deposit", "NRE deposit", "tax-free returns"],
    defaultAmount: 500000,
    maxAmount: 100000000,
    defaultRate: 7,
    defaultYears: 5,
    maxYears: 20,
    whatIs: "NRE (Non-Resident External) Fixed Deposits are cornerstone products for NRIs managing rupee savings in India, offering complete tax exemption on interest income for NRIs earning from foreign sources, combined with full repatriation rights allowing unrestricted international fund transfers anytime. Banks offer competitive rates 6.5-7.5% per annum with minimum investment ₹1 lakh and no upper limit. Tax-free status makes NRE FDs attractive for high-income NRIs seeking to minimize tax burden while earning predictable returns. Understanding NRE FD benefits is crucial for optimal NRI financial planning, FEMA regulations compliance, and effective rupee asset management.",
    faqs: [
      { question: "Is NRE FD interest income tax-free for NRIs?", answer: "Yes, if earnings are from outside India (foreign salary, foreign business). Interest earned on NRE FDs is completely tax-exempt for NRIs under India's tax laws." },
      { question: "Can NRE FD funds be repatriated outside India?", answer: "Yes, fully repatriable. NRE funds can be transferred to overseas accounts anytime. This flexibility is key advantage over NRO accounts." },
      { question: "What's the minimum investment for NRE FD in India?", answer: "Usually ₹1 lakh minimum. Maximum unlimited. Most banks offer attractive rates for bulk deposits (₹50L+): 7-7.5% p.a." },
      { question: "What happens to NRE FD if I return to India as resident?", answer: "You can keep NRE FD operational as resident, but tax benefits stop. Interest becomes taxable. Consider converting to regular FD or withdrawing." },
      { question: "Can NRE FD be pledged for loan in India?", answer: "Yes, NRE FD can be mortgaged for loans (NRE loan against deposit). Loan rate typically 1-2% above FD rate; flexible repayment terms." },
    ],
    relatedSlugs: ["nri/nro-fd-calculator", "nri/fcnr-deposit-calculator", "nri/nri-investment-comparison"],
  }),

  // 2. NRO FD Calculator
  createCompoundInterestTool({
    slug: "nro-fd-calculator",
    category: "nri",
    title: "NRO Fixed Deposit Calculator",
    shortTitle: "NRO FD Returns",
    description: "Calculate tax on NRO FD returns (30% slab, non-repatriable rupees)",
    keywords: ["NRO FD", "fixed deposit", "NRO deposit", "rupee deposit"],
    defaultAmount: 500000,
    maxAmount: 100000000,
    defaultRate: 6.5,
    defaultYears: 5,
    maxYears: 20,
    whatIs: "NRO (Non-Resident Ordinary) Fixed Deposits serve as rupee accounts for income earned within India or inherited from Indian sources—fundamentally different from NRE deposits. Interest income is taxable at progressive slab rate (10-30% for NRIs) plus 4% cess, making effective taxation 15-33%. While returns are lower than NRE (6-6.5%), NRO deposits are essential for managing India-sourced income like rental receipts, pensions, or family allowances. Principal is generally non-repatriable, making NRO suitable specifically for maintaining liquidity for India-based expenses. Understanding tax mechanics and compliance requirements helps optimize dual-account strategy effectively.",
    faqs: [
      { question: "Is NRO FD interest income taxable for NRIs?", answer: "Yes, fully taxable at progressive rates (10-30% depending on total income). TDS is deducted at source (20-30%). File ITR to claim credit." },
      { question: "Can NRO FD funds be repatriated outside India?", answer: "Generally no for principal. Interest earned can be repatriated (after tax). This non-repatriable nature makes NRO less flexible than NRE." },
      { question: "What should I use NRO FD for in India?", answer: "Holding income earned in India: rental income, pension, family support money. Use for India expenses rather than overseas transfer." },
      { question: "What's the tax rate on NRO FD interest in India?", answer: "Progressive slab rate (10-30%) + 4% cess. Effective rate: 22-33% total. TDS deducted at 20% upfront; file ITR for adjustment." },
      { question: "Should I keep NRO or NRE FD for India rental income?", answer: "NRO for rental income earned in India. NRE for overseas salary. Keep separate accounts; NRO tracks India-sourced income for tax purposes." },
    ],
    relatedSlugs: ["nri/nre-fd-calculator", "nri/fcnr-deposit-calculator", "nri/nri-rental-income-tax"],
  }),

  // 3. FCNR Deposit Calculator
  {
    slug: "fcnr-deposit-calculator",
    category: "nri",
    title: "FCNR(B) Deposit Calculator",
    shortTitle: "FCNR Deposit Returns",
    description: "Diversify with FCNR deposits in USD, GBP, EUR (2-3.5% p.a.)",
    keywords: ["FCNR", "foreign currency deposit", "forex deposit", "NRI foreign currency"],
    inputs: [
      { type: "number", key: "amount", label: "Investment Amount", prefix: "$", min: 1000, max: 1000000, step: 1000, default: 50000 },
      { type: "select", key: "currency", label: "Currency", options: [{ value: "usd", label: "USD" }, { value: "gbp", label: "GBP" }, { value: "eur", label: "EUR" }], default: "usd" },
      { type: "number", key: "years", label: "Investment Period", suffix: "years", min: 1, max: 10, step: 1, default: 5 },
      { type: "number", key: "rate", label: "Interest Rate", suffix: "% p.a.", min: 1, max: 5, step: 0.25, default: 2.5 },
      { type: "number", key: "inrExchange", label: "Current USD to INR Rate", suffix: "INR/USD", min: 70, max: 100, step: 0.5, default: 83 },
    ],
    calculate(v) {
      const amount = v.amount as number;
      const years = v.years as number;
      const rate = (v.rate as number) / 100;
      const exchange = v.inrExchange as number;

      const maturityAmount = amount * Math.pow(1 + rate, years);
      const interest = maturityAmount - amount;
      const maturityInINR = Math.round(maturityAmount * exchange);
      const interestInINR = Math.round(interest * exchange);

      return {
        maturityAmount: Math.round(maturityAmount * 100) / 100,
        interest: Math.round(interest * 100) / 100,
        maturityInINR,
        interestInINR
      };
    },
    resultFields: [
      { key: "maturityAmount", label: "Maturity Amount", format: "currency", isPrimary: true },
      { key: "maturityInINR", label: "Maturity in INR", format: "currency" },
      { key: "interest", label: "Interest Earned", format: "currency" },
      { key: "interestInINR", label: "Interest in INR", format: "currency" },
    ],
    content: {
      whatIs: "FCNR(B) Deposits represent specialized avenue for NRIs to maintain foreign currency assets within India while benefiting from repatriation rights and reasonable returns. Available in major currencies (USD, GBP, EUR, AUD, CAD, JPY, SGD), FCNR deposits typically earn 2-3.5% per annum—lower than rupee rates but competitive for foreign currency holdings. Primary advantage lies in hedging against rupee depreciation; if rupee weakens, your dollar-denominated deposit maintains purchasing power while rupee-based deposits lose value. For NRIs earning in foreign currency, FCNR deposits offer natural, tax-efficient holding vehicle without conversion timing concerns or repatriation restrictions.",
      faqs: [
        { question: "Is FCNR interest tax-free for NRIs?", answer: "Yes, for NRIs with foreign income. Interest is exempt from Indian tax if earnings are from outside India. Ensure TDS exemption declaration." },
        { question: "Can FCNR funds be fully repatriated?", answer: "Yes, principal and interest both fully repatriable to overseas accounts. No restriction on taking funds out, unlike NRO." },
        { question: "What currencies are available for FCNR in India?", answer: "Major currencies: USD, GBP, EUR, AUD, CAD, JPY, SGD. Availability varies by bank; USD most common and preferred." },
        { question: "How is FCNR interest rate determined in India?", answer: "FCNR rates are lower than rupee rates (2-3.5% vs 6-7%) due to currency risk and RBI policy. Rates vary by currency and bank." },
        { question: "Should I choose FCNR or NRE for overseas savings?", answer: "FCNR for foreign currency savings (USD salary); NRE for rupee savings. FCNR hedges rupee depreciation; NRE hedges expense inflation." },
      ],
      relatedSlugs: ["nri/nre-fd-calculator", "nri/nro-fd-calculator", "nri/forex-converter"],
    },
  },

  // 4. NRI Home Loan EMI
  createEMITool({
    slug: "nri-home-loan-emi",
    category: "nri",
    title: "NRI Home Loan EMI Calculator",
    shortTitle: "NRI Home Loan EMI",
    description: "Calculate EMI for NRI home loans with 8.5-9.5% interest rates",
    keywords: ["NRI home loan", "home loan EMI", "NRI mortgage", "property loan"],
    defaultAmount: 2000000,
    maxAmount: 500000000,
    amountStep: 1000000,
    amountLabel: "Loan Amount",
    defaultRate: 9,
    defaultTenure: 20,
    maxTenure: 30,
    whatIs: "NRI home loans in India come with special considerations reflecting increased documentation and currency risks. Interest rates typically range from 8.5-9.5% per annum—approximately 0.5-1% higher than resident rates (7-8.5%) due to complexity in income verification, employment stability assessment across borders, and forex exposure. NRIs can generally borrow up to 70-80% of property value through most banks, with loan amounts extending to ₹5 crore for premium metro properties. Tenures extend to 30 years, providing flexibility in EMI structuring. Understanding LTV ratios, required documentation, and approval timelines helps NRI homebuyers plan purchases effectively and compare across lenders.",
    faqs: [
      { question: "What's the interest rate for NRI home loans in India?", answer: "Typically 8.5-9.5% p.a. (0.5-1% higher than resident rates). Varies by bank, income source, employment stability, and property location." },
      { question: "How much can an NRI borrow for home purchase in India?", answer: "Usually 70-80% LTV (Loan-to-Value). For ₹50L property: borrow ₹35-40L. For ₹1Cr+: upto ₹80L depending on income proof." },
      { question: "What documents do NRIs need for home loan in India?", answer: "Passport, visa, NRI certificate, employment letter, salary proof (6-12 months statements), ITR, property documents, insurance." },
      { question: "Can NRI get home loan for investment property in India?", answer: "Yes, most banks allow. For self-occupied: easy approval. For rental property: stricter due diligence, higher rates (0.5% extra)." },
      { question: "What's the home loan approval timeline for NRIs in India?", answer: "30-45 days typically (longer than residents: 10-15 days). Delays due to document verification, fund source checks, currency conversion." },
    ],
    relatedSlugs: ["property/home-loan-emi", "nri/nri-property-tax", "nri/repatriation-calculator"],
  }),

  // 5. NRI TDS on Property Sale
  createPercentageTool({
    slug: "nri-tds-property",
    category: "nri",
    title: "TDS on NRI Property Sale Calculator",
    shortTitle: "Property Sale TDS",
    description: "Calculate TDS on NRI property sale with 20% LTCG tax rate",
    keywords: ["NRI property sale", "TDS", "capital gains tax", "property tax"],
    amountLabel: "Sale Price",
    rateLabel: "LTCG Tax Rate",
    resultLabel: "TDS Amount",
    defaultAmount: 5000000,
    maxAmount: 100000000,
    defaultRate: 20,
    whatIs: "Long-term capital gains (LTCG) taxation on NRI property sales follows India's capital gains framework, applying flat 20% rate to gains from properties held exceeding two years. Tax Deducted at Source (TDS) of 20% is automatically deducted by property buyer during registration, requiring NRI sellers to file Income Tax Returns (ITR) for adjustment. Taxable gain is calculated as: sale price minus purchase price minus transaction costs minus indexation benefit. Indexation benefit significantly reduces taxable gain by applying inflation factors from purchase to sale year. For NRIs with property holdings, understanding these rules and claiming available deductions prevents overpayment and ensures optimal cash flow from property sales.",
    faqs: [
      { question: "What's the tax rate on NRI property sale in India?", answer: "LTCG (>2 years): 20% flat. STCG (<2 years): 30% + 4% cess. Indexation benefit reduces gain further. Effective rate: 15-25% for LTCG." },
      { question: "How is indexation benefit calculated for NRI property?", answer: "Cost of Acquisition × Indexation Factor (based on inflation). E.g., ₹50L property purchased 2015, sold 2024: Cost indexed to ₹75L, reducing taxable gain." },
      { question: "Can NRI claim capital loss on property sale in India?", answer: "Yes, LTCG loss can offset other income. Loss carry-forward upto 8 years. File ITR even if no tax payable to claim loss benefit." },
      { question: "Is there double taxation for NRI property sale?", answer: "Possibly. India taxes property gains; native country also may tax. Use DTAA (Double Tax Avoidance Agreement) to get credit for taxes paid in India." },
      { question: "What documents needed for NRI property sale tax filing in India?", answer: "Property deed, sale agreement, property valuation report, TDS certificate (from buyer), proof of purchase price, renovation costs, payment proof." },
    ],
    additionalResults: (v) => {
      const salePrice = v.amount as number;
      const taxRate = (v.rate as number) / 100;
      const tdsAmount = Math.round(salePrice * taxRate);
      const netProceeds = salePrice - tdsAmount;
      return { netProceeds };
    },
    additionalResultFields: [
      { key: "netProceeds", label: "Net Proceeds After TDS", format: "currency" },
    ],
    relatedSlugs: ["nri/dtaa-relief-calculator", "nri/nri-property-tax", "property/property-valuation-calculator"],
  }),

  // 6. NRI Rental Income Tax
  {
    slug: "nri-rental-income-tax",
    category: "nri",
    title: "NRI Rental Income Tax Calculator",
    shortTitle: "Rental Income Tax",
    description: "Calculate tax on rental income at flat 30% slab rate",
    keywords: ["NRI rental income", "rental tax", "income tax", "property income"],
    inputs: [
      { type: "number", key: "annualRental", label: "Annual Rental Income", prefix: "₹", min: 100000, max: 50000000, step: 100000, default: 1200000 },
      { type: "number", key: "maintenanceCost", label: "Annual Maintenance & Repair", prefix: "₹", min: 0, max: 5000000, step: 50000, default: 120000 },
      { type: "number", key: "propertyTax", label: "Annual Property Tax", prefix: "₹", min: 0, max: 1000000, step: 10000, default: 60000 },
      { type: "number", key: "interestLoan", label: "Home Loan Interest", prefix: "₹", min: 0, max: 10000000, step: 50000, default: 300000 },
      { type: "number", key: "depreciation", label: "Depreciation (5% of construction cost)", prefix: "₹", min: 0, max: 5000000, step: 50000, default: 0 },
    ],
    calculate(v) {
      const rental = v.annualRental as number;
      const maintenance = v.maintenanceCost as number;
      const tax = v.propertyTax as number;
      const interest = v.interestLoan as number;
      const depreciation = v.depreciation as number;

      const totalDeductions = maintenance + tax + interest + depreciation;
      const netIncome = Math.max(0, rental - totalDeductions);

      // Standard deduction: 30% of rental income
      const standardDeduction = Math.round(rental * 0.30);
      const taxableIncome = Math.max(0, netIncome - standardDeduction);

      // NRI tax: 30% slab + 4% cess
      const incomeTax = Math.round(taxableIncome * 0.30);
      const cess = Math.round(incomeTax * 0.04);
      const totalTax = incomeTax + cess;

      return { rental, totalDeductions, netIncome, standardDeduction, taxableIncome, totalTax, incomeTax };
    },
    resultFields: [
      { key: "totalTax", label: "Total Tax Payable", format: "currency", isPrimary: true },
      { key: "taxableIncome", label: "Taxable Income", format: "currency" },
      { key: "netIncome", label: "Net Income (before 30% deduction)", format: "currency" },
      { key: "totalDeductions", label: "Total Deductions", format: "currency" },
    ],
    content: {
      whatIs: "NRI rental income is taxed at 30% flat rate (plus 4% cess = 31.2% total) without progressive slab benefit. Deductions allowed: maintenance, repair, property tax, home loan interest (on borrowed amount), depreciation (5% of construction cost), and standard deduction (30% of rental income). Net income calculation: Rental - Deductions. NRIs must file ITR, pay TDS (30%), and may claim DTAA credit if income taxed in native country too.",
      faqs: [
        { question: "What's the income tax rate for NRI rental income in India?", answer: "Flat 30% rate + 4% cess = 31.2% total. No progressive slab benefit (unlike residents: 5-30% slab). Much higher tax burden for NRIs." },
        { question: "What deductions are allowed on NRI rental income in India?", answer: "Maintenance & repair, property tax, home loan interest, depreciation (5% of construction cost), standard deduction (30% of rental). Not allowed: own rent, children's education." },
        { question: "How is TDS deducted on NRI rental income?", answer: "Tenant pays TDS (30% of rent) to government directly. NRI files ITR to adjust TDS against total tax liability. Overpayment refunded." },
        { question: "Can NRI claim capital loss on property against rental income?", answer: "No, capital loss from property cannot offset rental income in same year. But LTCG loss can carry forward 8 years." },
        { question: "Should NRI file ITR for rental income in India?", answer: "Yes, mandatory. Even if tax is deducted at source, file ITR to claim deductions, adjust TDS, and maintain tax record for DTAA benefits." },
      ],
      relatedSlugs: ["nri/nri-property-tax", "nri/nri-tds-property", "nri/dtaa-relief-calculator"],
    },
  },

  // 7. Repatriation Calculator
  {
    slug: "repatriation-calculator",
    category: "nri",
    title: "Repatriation Limit & Cost Calculator",
    shortTitle: "Repatriation Limits",
    description: "Calculate repatriation costs and limits for NRI fund transfers",
    keywords: ["repatriation", "NRI repatriation", "fund transfer limit", "forex limit"],
    inputs: [
      { type: "number", key: "transferAmount", label: "Amount to Transfer", prefix: "₹", min: 100000, max: 100000000, step: 100000, default: 5000000 },
      { type: "select", key: "sourceType", label: "Source of Funds", options: [{ value: "salary", label: "Foreign Salary/Income" }, { value: "savings", label: "NRE/FCNR Savings" }, { value: "earnings", label: "Investment Earnings" }], default: "salary" },
      { type: "number", key: "bankChargePercent", label: "Bank Charge %", suffix: "%", min: 0, max: 2, step: 0.1, default: 0.5 },
    ],
    calculate(v) {
      const amount = v.transferAmount as number;
      const source = v.sourceType as string;
      const chargePercent = (v.bankChargePercent as number) / 100;

      const bankCharge = Math.round(amount * chargePercent);
      const netTransfer = amount - bankCharge;

      let limit = "Unlimited";
      let note = "";

      if (source === "salary") {
        limit = "Unlimited (earned income)";
        note = "Foreign salary can be fully transferred. Provide employer letter and salary proof.";
      } else if (source === "savings") {
        limit = "Unlimited (from NRE/FCNR)";
        note = "NRE/FCNR are fully repatriable. No restriction on transfer amount.";
      } else {
        limit = "Check FEMA rules";
        note = "Investment earnings may have limits depending on type. Consult bank for current limits.";
      }

      return { transferAmount: amount, bankCharge, netTransfer, limit: limit as any, dailyExchangeRate: 83 };
    },
    resultFields: [
      { key: "netTransfer", label: "Net Amount Transferred", format: "currency", isPrimary: true },
      { key: "transferAmount", label: "Gross Transfer Amount", format: "currency" },
      { key: "bankCharge", label: "Bank Charges", format: "currency" },
      { key: "limit", label: "Repatriation Limit Status", format: "text" },
    ],
    content: {
      whatIs: "Repatriation is transferring NRI funds from India to overseas accounts. Most funds are freely repatriable: foreign salary (unlimited), NRE deposits (unlimited), investment earnings (subject to limits). FCNR deposits are fully repatriable. RBI allows unlimited repatriation of income earned outside India. Bank charges typically 0.3-0.75% depending on amount and bank. No forex limit for income earned abroad; limits apply only to certain inherited/gifted assets.",
      faqs: [
        { question: "Is there a limit on NRI fund repatriation from India?", answer: "No limit on income earned outside India (salary, forex business). NRE/FCNR deposits unlimited. Only inherited/gifted assets have restrictions (50% limit)." },
        { question: "What's the cost of repatriating funds from India?", answer: "Bank charges: 0.3-0.75% depending on bank and amount. No government tax on repatriation. Swift fee (300-500 INR) charged by bank." },
        { question: "How long does repatriation take from India?", answer: "Usually 2-4 business days. Wire transfer via Swift takes 1-2 days to clear at destination. Some banks offer same-day transfer." },
        { question: "What documents needed for NRI fund repatriation?", answer: "PAN card, passport, NRI certificate, bank statement (for income proof), ITR (if requested), employment letter, source of funds proof." },
        { question: "Are inherited assets in India fully repatriable for NRI?", answer: "Only 50% of inherited assets are repatriable. Remaining 50% stays in India. Gifts between family members are also restricted similarly." },
      ],
      relatedSlugs: ["nri/nre-fd-calculator", "nri/fcnr-deposit-calculator", "nri/forex-converter"],
    },
  },

  // 8. DTAA Relief Calculator
  {
    slug: "dtaa-relief-calculator",
    category: "nri",
    title: "DTAA Relief Calculator",
    shortTitle: "DTAA Tax Relief",
    description: "Calculate DTAA relief on double taxation for NRI income",
    keywords: ["DTAA", "double tax avoidance", "foreign tax credit", "tax treaty"],
    inputs: [
      { type: "number", key: "indianTax", label: "Tax Paid in India", prefix: "₹", min: 0, max: 50000000, step: 100000, default: 500000 },
      { type: "number", key: "foreignTax", label: "Tax Paid in Native Country", prefix: "₹", min: 0, max: 50000000, step: 100000, default: 600000 },
      { type: "number", key: "totalIncome", label: "Total Global Income", prefix: "₹", min: 100000, max: 100000000, step: 100000, default: 2000000 },
    ],
    calculate(v) {
      const indianTax = v.indianTax as number;
      const foreignTax = v.foreignTax as number;
      const income = v.totalIncome as number;

      const grossTax = indianTax + foreignTax;
      const allowedCredit = Math.min(indianTax, foreignTax); // DTAA limits foreign credit
      const netTaxPayable = grossTax - allowedCredit;
      const effectiveRate = Math.round((netTaxPayable / income) * 100);

      return { indianTax, foreignTax, allowedCredit, netTaxPayable, grossTax, effectiveRate: Math.round(effectiveRate) };
    },
    resultFields: [
      { key: "netTaxPayable", label: "Net Tax After DTAA Relief", format: "currency", isPrimary: true },
      { key: "grossTax", label: "Total Tax (Before DTAA)", format: "currency" },
      { key: "allowedCredit", label: "DTAA Credit Allowed", format: "currency" },
      { key: "effectiveRate", label: "Effective Tax Rate", format: "percent" },
    ],
    content: {
      whatIs: "DTAA (Double Tax Avoidance Agreement) provides relief when NRI income is taxed by both India and native country. India has DTAAs with 100+ countries. Generally, foreign taxes paid are credited against Indian taxes. Lower-taxed country's rate applies (most cases India at 30% NRI rate). Filing ITR in India and claiming FEMA exemption ensures compliance. DTAA terms vary; consult tax advisor for country-specific details.",
      faqs: [
        { question: "How does DTAA relief work for NRI taxes in India?", answer: "DTAA provides foreign tax credit: limits credit to lesser of Indian or foreign tax paid. Prevents double taxation. India-US treaty limits US tax credit to Indian tax liability." },
        { question: "Can NRI claim full foreign tax as credit in India?", answer: "Not always. DTAA credit is limited to Indian tax liability on same income. E.g., if Indian tax is ₹30L but foreign tax ₹40L, only ₹30L credited." },
        { question: "Which countries have DTAA with India?", answer: "Major countries: USA, UK, Canada, Australia, Singapore, Japan, Germany, France, Netherlands, UAE. India has 100+ DTAAs. Check India's tax website for full list." },
        { question: "Do NRIs need to file tax returns in both countries?", answer: "Yes, if income exceeds filing threshold in both countries. File ITR in India, returns in native country. DTAA prevents double taxation." },
        { question: "What documentation is needed for DTAA relief claim in India?", answer: "ITR filed in India, foreign tax return copy, DTAA certificate from native country's tax authority, proof of foreign tax payment, income documentation." },
      ],
      relatedSlugs: ["nri/nri-rental-income-tax", "nri/nri-tds-property", "nri/nri-mutual-fund-tax"],
    },
  },

  // 9. Forex Converter
  {
    slug: "forex-converter",
    category: "nri",
    title: "Currency Converter (INR to Major Currencies)",
    shortTitle: "Forex Converter",
    description: "Real-time exchange rate converter for INR and major currencies",
    keywords: ["forex", "currency converter", "INR conversion", "exchange rate"],
    inputs: [
      { type: "number", key: "inrAmount", label: "Amount in INR", prefix: "₹", min: 1000, max: 1000000000, step: 10000, default: 1000000 },
      { type: "select", key: "currency", label: "Convert To", options: [{ value: "usd", label: "USD ($)" }, { value: "gbp", label: "GBP (£)" }, { value: "eur", label: "EUR (€)" }, { value: "aud", label: "AUD (A$)" }, { value: "cad", label: "CAD (C$)" }, { value: "sgd", label: "SGD (S$)" }], default: "usd" },
      { type: "number", key: "exchangeRate", label: "Exchange Rate", suffix: "per ₹1", min: 0.001, max: 0.02, step: 0.0001, default: 0.012 },
    ],
    calculate(v) {
      const inr = v.inrAmount as number;
      const rate = v.exchangeRate as number;

      const converted = Math.round(inr * rate * 100) / 100;

      return { inrAmount: inr, convertedAmount: converted, exchangeRate: rate };
    },
    resultFields: [
      { key: "convertedAmount", label: "Converted Amount", format: "currency", isPrimary: true },
      { key: "inrAmount", label: "Original Amount (INR)", format: "currency" },
      { key: "exchangeRate", label: "Exchange Rate Used", format: "number" },
    ],
    content: {
      whatIs: "Currency converter helps NRIs calculate foreign currency equivalent of rupees for remittances, investments, and planning. Exchange rates fluctuate daily based on market conditions. As of 2024: 1 USD = 82-84 INR, 1 GBP = 105 INR, 1 EUR = 90 INR. Rates vary by bank; TCS (Tax Collected at Source) applies on forex purchases above ₹7L/financial year. HDFC, ICICI, Axis provide competitive forex rates.",
      faqs: [
        { question: "What's the current exchange rate INR to USD in 2024?", answer: "Approximately 1 USD = 82-84 INR (varies daily). Check live rates on bank websites, XE.com, or OANDA for real-time rates before converting." },
        { question: "Is there a cost to convert INR to foreign currency in India?", answer: "Yes, typically 0.3-0.75% forex markup by banks. TCS 20% applies on forex purchases >₹7L/year. Some banks offer better rates for bulk conversions." },
        { question: "What's the best way to convert rupees to foreign currency in India?", answer: "Banks offer best rates in bulk. Credit cards charge markup (1-2%). Money transfer apps (Wise, OFX) cheaper for small amounts. Negotiate for large transfers." },
        { question: "Should I convert INR to USD now or wait in India?", answer: "Unpredictable. If planning fixed expense, lock rate now. For flexible timeline, monitor trend over weeks. Rupee often depreciates 3-5% yearly." },
        { question: "What TCS is deducted on forex purchase in India?", answer: "20% TCS on forex purchase above ₹7L per financial year. Credited in tax return if TDS/taxes eligible. Advance return filing stops TCS deduction." },
      ],
      relatedSlugs: ["nri/remittance-cost-calculator", "nri/fcnr-deposit-calculator", "nri/nri-investment-comparison"],
    },
  },

  // 10. Remittance Cost Calculator
  {
    slug: "remittance-cost-calculator",
    category: "nri",
    title: "Remittance Cost Calculator",
    shortTitle: "Remittance Cost",
    description: "Calculate remittance costs and net amount for India transfers",
    keywords: ["remittance", "money transfer", "wire transfer", "remittance cost"],
    inputs: [
      { type: "number", key: "amount", label: "Amount to Send", prefix: "₹", min: 100000, max: 100000000, step: 100000, default: 1000000 },
      { type: "select", key: "method", label: "Transfer Method", options: [{ value: "swift", label: "SWIFT Bank Transfer" }, { value: "neft", label: "NEFT" }, { value: "app", label: "Money Transfer App" }, { value: "cheque", label: "Cheque" }], default: "swift" },
      { type: "number", key: "chargePercent", label: "Bank Charge %", suffix: "%", min: 0, max: 1, step: 0.1, default: 0.5 },
      { type: "number", key: "flatFee", label: "Flat Processing Fee", prefix: "₹", min: 0, max: 5000, step: 500, default: 2000 },
    ],
    calculate(v) {
      const amount = v.amount as number;
      const method = v.method as string;
      const chargePercent = (v.chargePercent as number) / 100;
      const fee = v.flatFee as number;

      const percentCharge = Math.round(amount * chargePercent);
      const totalCost = percentCharge + fee;
      const netReceived = amount - totalCost;
      const costPercent = Math.round((totalCost / amount) * 100);

      return { totalCost, netReceived, percentCharge, fee, method, costPercent };
    },
    resultFields: [
      { key: "netReceived", label: "Amount Received in India", format: "currency", isPrimary: true },
      { key: "totalCost", label: "Total Remittance Cost", format: "currency" },
      { key: "costPercent", label: "Cost as % of Transfer", format: "percent" },
      { key: "percentCharge", label: "Percentage Charge", format: "currency" },
    ],
    content: {
      whatIs: "Remittance cost includes bank charges (0.25-0.75%), flat processing fees (₹1,500-3,000), and intermediary bank fees (₹500-1,500). Methods: SWIFT (slowest, expensive), NEFT (fast, cheaper), Money Transfer Apps (fastest, cheapest: 1-2% total), Cheque (very slow). Apps like Wise, OFX, Remitly charge 1-2% total; traditional banks 1-1.5% + fees. Choosing right method saves 10-50% on remittance costs.",
      faqs: [
        { question: "What's the cheapest way to send money to India from abroad?", answer: "Money transfer apps (Wise, OFX, Remitly): 1-2% cost. NEFT: 0.5-1% + fees. SWIFT: 0.75-1.5% + fees. Apps fastest and cheapest for regular transfers." },
        { question: "How long does money transfer to India take?", answer: "Apps: 2-4 hours. NEFT: 2-4 hours. SWIFT: 2-3 business days. Apps and NEFT fastest; SWIFT slower but lower cost for large amounts." },
        { question: "Are there limits on remittance to India?", answer: "No government limit on income from abroad remittance. Liberalized Remittance Scheme allows ₹10L/financial year for capital account transactions." },
        { question: "Is remittance amount taxable for NRI in India?", answer: "No tax on remittance receipt in India if funds are from foreign income. But funds' investment in India may generate taxable income." },
        { question: "Should NRI remit to NRO or NRE account in India?", answer: "NRE for savings (tax-free); NRO for income to be used in India. Most remit to NRE and transfer to NRO as needed for India expenses." },
      ],
      relatedSlugs: ["nri/forex-converter", "nri/repatriation-calculator", "nri/nre-fd-calculator"],
    },
  },

  // 11. NRI Investment Comparison
  {
    slug: "nri-investment-comparison",
    category: "nri",
    title: "NRE vs NRO vs FCNR Investment Comparison",
    shortTitle: "Investment Options Comparison",
    description: "Compare returns across NRE, NRO, and FCNR investment options",
    keywords: ["NRE vs NRO", "investment comparison", "deposit comparison", "NRI savings"],
    inputs: [
      { type: "number", key: "investmentAmount", label: "Investment Amount", prefix: "₹", min: 500000, max: 50000000, step: 500000, default: 5000000 },
      { type: "number", key: "years", label: "Investment Period", suffix: "years", min: 1, max: 20, step: 1, default: 5 },
    ],
    calculate(v) {
      const amount = v.investmentAmount as number;
      const years = v.years as number;

      // NRE: 7% tax-free
      const nreReturn = 0.07;
      const nreMaturity = Math.round(amount * Math.pow(1 + nreReturn, years));
      const nreInterest = nreMaturity - amount;

      // NRO: 6.5% taxable at 30% = 4.55% net
      const nroReturn = 0.065;
      const nroMaturity = Math.round(amount * Math.pow(1 + nroReturn, years));
      const nroGrossInterest = nroMaturity - amount;
      const nroTax = Math.round(nroGrossInterest * 0.30);
      const nroNetInterest = nroGrossInterest - nroTax;
      const nroNetMaturity = nroMaturity - nroTax;

      // FCNR: 2.5% tax-free (in USD)
      const fcnrReturn = 0.025;
      const fcnrMaturity = Math.round(amount * Math.pow(1 + fcnrReturn, years));
      const fcnrInterest = fcnrMaturity - amount;

      return {
        nreMaturity,
        nreInterest,
        nroNetMaturity,
        nroNetInterest,
        fcnrMaturity,
        fcnrInterest,
        bestOption: nreMaturity > nroNetMaturity ? "NRE" : "NRO"
      };
    },
    resultFields: [
      { key: "nreMaturity", label: "NRE Maturity Amount", format: "currency" },
      { key: "nroNetMaturity", label: "NRO Net Maturity (After Tax)", format: "currency" },
      { key: "fcnrMaturity", label: "FCNR Maturity (USD converted)", format: "currency" },
      { key: "bestOption", label: "Best Option", format: "text", isPrimary: true },
    ],
    content: {
      whatIs: "NRE, NRO, and FCNR serve different purposes. NRE (7% tax-free): for foreign income, fully repatriable, best for savings. NRO (6.5% taxable at 30%): for India-sourced income, non-repatriable principal, use for India expenses. FCNR (2.5% in USD): currency hedge, fully repatriable, protects against rupee depreciation. Choose based on income source, repatriation need, and currency needs. NRE typically best for most NRIs.",
      faqs: [
        { question: "Which deposit type is best for NRI in India?", answer: "NRE if you want to save and repatriate: 7% tax-free returns. NRO if maintaining India expenses from rental/pension income. FCNR if earning in USD and want currency hedge." },
        { question: "Can I maintain both NRE and NRO accounts in India?", answer: "Yes, most NRIs maintain both. NRE for savings from foreign income, NRO for India income. Completely separate accounts with different rules." },
        { question: "Is FCNR better than NRE for currency protection?", answer: "FCNR hedges rupee depreciation (earn 2.5% + currency gain). NRE doesn't hedge but earns higher rate (7%). If rupee weakens significantly, FCNR wins." },
        { question: "Can I convert NRE to NRO or vice versa in India?", answer: "No direct conversion. Must close one and open new account (costs time and documentation). However, transfers between accounts are allowed in some cases." },
        { question: "What happens to NRE deposit if I return to India as resident?", answer: "NRE continues earning 7% even as resident, but interest becomes taxable. Can keep it, convert to NRO, or withdraw. Review with tax advisor." },
      ],
      relatedSlugs: ["nri/nre-fd-calculator", "nri/nro-fd-calculator", "nri/fcnr-deposit-calculator"],
    },
  },

  // 12. OCI Card Fee Calculator
  {
    slug: "oci-card-fee-calculator",
    category: "nri",
    title: "OCI Card Application Fee Calculator",
    shortTitle: "OCI Card Fees",
    description: "Calculate OCI card application and renewal fees and timelines",
    keywords: ["OCI card", "overseas citizen", "visa fee", "OCI application"],
    inputs: [
      { type: "number", key: "applicantAge", label: "Applicant Age", suffix: "years", min: 1, max: 100, step: 1, default: 35 },
      { type: "select", key: "feeType", label: "Fee Type", options: [{ value: "new", label: "New Application" }, { value: "renewal", label: "Renewal (after 20 years)" }, { value: "child", label: "Child under 18" }], default: "new" },
    ],
    calculate(v) {
      const age = v.applicantAge as number;
      const type = v.feeType as string;

      let applicationFee = 0;
      let processingFee = 0;
      let total = 0;

      if (type === "new") {
        applicationFee = age < 18 ? 0 : 3000;
        processingFee = age < 18 ? 0 : 5000;
      } else if (type === "renewal") {
        applicationFee = 500;
        processingFee = 2500;
      } else if (type === "child") {
        applicationFee = 0;
        processingFee = 500;
      }

      total = applicationFee + processingFee;

      return { applicationFee, processingFee, total, validity: type === "renewal" ? "20 years" : "Lifetime" };
    },
    resultFields: [
      { key: "total", label: "Total Fee", format: "currency", isPrimary: true },
      { key: "applicationFee", label: "Application Fee", format: "currency" },
      { key: "processingFee", label: "Processing Fee", format: "currency" },
      { key: "validity", label: "Validity Period", format: "text" },
    ],
    content: {
      whatIs: "OCI (Overseas Citizen of India) card provides multiple-entry, lifetime visa-free travel to India with same privileges as Indian citizens except voting, purchasing agricultural land, and government jobs. Application fee: ₹3,000 (adults), free (children <18). Processing fee: ₹5,000. Renewal every 20 years: ₹500 + ₹2,500. Processing time: 4-6 weeks. Available online through VFS at Indian embassies/consulates worldwide.",
      faqs: [
        { question: "Who is eligible for OCI card in India?", answer: "Any person of Indian origin: born in India, had Indian citizenship, or parent/grandparent was Indian citizen. NRIs most eligible group." },
        { question: "How long is OCI card valid in India?", answer: "Lifetime validity. No need to renew unless passport changes. OCI printed on passport; renew only if getting new passport." },
        { question: "Can OCI holder work in India?", answer: "Yes, can work in India but not in government/defense sector, voting positions. Can own business, property (except agricultural land), and invest freely." },
        { question: "What's the difference between OCI and NRI status?", answer: "OCI: citizenship-like status, lifetime, multiple-entry visa. NRI: tax resident status based on stay. OCI is immigration status; NRI is tax classification." },
        { question: "How much does OCI card cost in total?", answer: "Application: ₹3,000, Processing: ₹5,000, Total ₹8,000 + VFS service charge (₹500-1,000). Valid lifetime; one-time investment." },
      ],
      relatedSlugs: ["nri/nri-property-tax", "nri/nri-investment-comparison", "nri/repatriation-calculator"],
    },
  },

  // 13. NRI Property Tax
  {
    slug: "nri-property-tax",
    category: "nri",
    title: "NRI Property Tax Obligations Calculator",
    shortTitle: "Property Tax",
    description: "Calculate property tax on residential holdings in Indian cities",
    keywords: ["property tax", "NRI property", "real estate tax", "municipal tax"],
    inputs: [
      { type: "number", key: "propertyValue", label: "Property Value", prefix: "₹", min: 1000000, max: 100000000, step: 1000000, default: 5000000 },
      { type: "number", key: "propertyTaxRate", label: "Municipal Tax Rate", suffix: "% of value", min: 0.1, max: 2, step: 0.1, default: 0.5 },
      { type: "toggle", key: "isRental", label: "Is Rental Property", default: false },
    ],
    calculate(v) {
      const value = v.propertyValue as number;
      const rate = (v.propertyTaxRate as number) / 100;
      const isRental = v.isRental as boolean;

      const annualTax = Math.round(value * rate);
      const quarterlyTax = Math.round(annualTax / 4);

      let additionalObligations = "Annual property tax filing required. ";
      if (isRental) {
        additionalObligations += "Also file rental income tax return (ITR). ";
      }
      additionalObligations += "Use local municipal website or bank for payment.";

      return { annualTax, quarterlyTax, obligations: additionalObligations as any };
    },
    resultFields: [
      { key: "annualTax", label: "Annual Property Tax", format: "currency", isPrimary: true },
      { key: "quarterlyTax", label: "Quarterly Tax (if applicable)", format: "currency" },
      { key: "obligations", label: "Tax Obligations", format: "text" },
    ],
    content: {
      whatIs: "NRI property owners must pay annual property tax to municipal corporation (₹5,000-50,000+ depending on value). Tax rate: 0.3-0.8% of property value. Paid to local municipal authority via online portals or banks. Non-payment leads to penalties and auction. NRI can authorize power-of-attorney to handle payments. Also file property in ITR declaration if rental or for wealth statement purposes.",
      faqs: [
        { question: "Is property tax mandatory for NRI property owners in India?", answer: "Yes, annual property tax to municipal corporation is mandatory. Non-payment leads to penalties, interest, and eventual property auction." },
        { question: "How much property tax does NRI pay in India?", answer: "0.3-0.8% of property value annually, varies by city and municipal rules. Example: ₹50L property = ₹1,500-4,000/year tax." },
        { question: "How can NRI pay property tax from abroad?", answer: "Online: municipal website (most cities). Via bank: authorized Indian bank branches. Via power-of-attorney: authorize India-based person to pay on behalf." },
        { question: "Can NRI get tax benefits on property expenses in India?", answer: "Limited. Home loan interest deduction available. Rental property: maintenance, tax, loan interest deductible. No depreciation benefit for self-occupied." },
        { question: "What if NRI doesn't pay property tax in India?", answer: "Penalties (2-5% yearly), interest accrual, legal notice from municipality, eventual property auction. Reputation damaged for future borrowing." },
      ],
      relatedSlugs: ["nri/nri-rental-income-tax", "nri/nri-tds-property", "property/home-loan-emi"],
    },
  },

  // 14. TCS on Remittance
  {
    slug: "tcs-on-remittance",
    category: "nri",
    title: "TCS on Foreign Remittance Calculator",
    shortTitle: "TCS on Remittance",
    description: "Calculate Tax Collected at Source on overseas remittances",
    keywords: ["TCS", "tax collected at source", "forex TCS", "remittance tax"],
    inputs: [
      { type: "number", key: "annualRemittance", label: "Annual Forex Purchase/Remittance", prefix: "₹", min: 1000000, max: 500000000, step: 1000000, default: 10000000 },
      { type: "select", key: "reasonType", label: "Reason for Remittance", options: [{ value: "education", label: "Education Abroad" }, { value: "medical", label: "Medical Treatment" }, { value: "travel", label: "Travel/Tourism" }, { value: "other", label: "Other" }], default: "other" },
    ],
    calculate(v) {
      const remittance = v.annualRemittance as number;
      const reason = v.reasonType as string;

      const tcsThreshold = 7000000; // ₹7 lakh threshold
      const tcsExemptLimit = 7000000;

      let tcsRate = 0;
      let tcsAmount = 0;

      if (remittance > tcsExemptLimit) {
        const exemptAmount = Math.min(remittance, tcsExemptLimit);
        const aboveExempt = remittance - exemptAmount;

        if (reason === "education" || reason === "medical") {
          tcsRate = 0.05; // 5% for education/medical
          tcsAmount = Math.round(aboveExempt * tcsRate);
        } else {
          tcsRate = 0.20; // 20% for other purposes
          tcsAmount = Math.round(aboveExempt * tcsRate);
        }
      }

      const netRemittance = remittance - tcsAmount;

      return { remittance, tcsAmount, tcsRate: Math.round(tcsRate * 100), netRemittance, tcsExemptLimit };
    },
    resultFields: [
      { key: "tcsAmount", label: "TCS Deducted", format: "currency", isPrimary: true },
      { key: "netRemittance", label: "Net Remittance Amount", format: "currency" },
      { key: "remittance", label: "Gross Remittance", format: "currency" },
      { key: "tcsRate", label: "TCS Rate Applied", format: "percent" },
    ],
    content: {
      whatIs: "TCS (Tax Collected at Source) is deducted on forex purchases above ₹7L/financial year. Rate: 5% for education/medical abroad, 20% for other purposes. TCS threshold: ₹7L/year (April-March). E.g., purchasing $10k USD at ₹83/USD with 20% TCS: ₹8.3L remittance, TCS ₹10.6k. TCS is collected upfront at bank, credited in tax return if eligible. High earners file advance returns to avoid TCS.",
      faqs: [
        { question: "When is TCS collected on forex purchase in India?", answer: "When forex amount exceeds ₹7L in a financial year (April-March). Every rupee beyond threshold attracts TCS at 5% or 20%." },
        { question: "Is TCS same for education and general travel in India?", answer: "No. Education/medical: 5% TCS. Travel/other: 20% TCS. Provide proof to bank for education/medical to get lower rate." },
        { question: "Can TCS be avoided on forex in India?", answer: "Yes, file Form 15G/15H (exemption certificate) if income below taxable limit. Also file advance return before fiscal year-end to claim exemption." },
        { question: "Is TCS credited back in tax return in India?", answer: "Yes, TCS collected is automatically credited against tax liability. Refund issued if TCS > tax payable. File ITR to claim refund." },
        { question: "How much TCS is charged per dollar of remittance in India?", answer: "TCS depends on rupee amount, not dollar amount. ₹7L threshold applies. Beyond ₹7L: 5% (education) or 20% (other) on additional amount." },
      ],
      relatedSlugs: ["nri/forex-converter", "nri/remittance-cost-calculator", "nri/repatriation-calculator"],
    },
  },

  // 15. NRI Mutual Fund Tax
  {
    slug: "nri-mutual-fund-tax",
    category: "nri",
    title: "NRI Mutual Fund Tax Calculator",
    shortTitle: "Mutual Fund Tax",
    description: "Calculate tax on NRI mutual fund returns and dividends",
    keywords: ["NRI mutual fund", "mutual fund tax", "capital gains tax", "fund taxation"],
    inputs: [
      { type: "number", key: "investmentAmount", label: "Investment Amount", prefix: "₹", min: 100000, max: 50000000, step: 100000, default: 1000000 },
      { type: "number", key: "realisedGain", label: "Realised Gains on Redemption", prefix: "₹", min: 0, max: 50000000, step: 100000, default: 300000 },
      { type: "select", key: "fundType", label: "Fund Type", options: [{ value: "equity", label: "Equity Fund" }, { value: "debt", label: "Debt Fund" }, { value: "balanced", label: "Balanced Fund" }], default: "equity" },
      { type: "select", key: "holdingPeriod", label: "Holding Period", options: [{ value: "short", label: "Less than 12 months (Short-term)" }, { value: "long", label: "More than 12 months (Long-term)" }], default: "long" },
    ],
    calculate(v) {
      const gain = v.realisedGain as number;
      const type = v.fundType as string;
      const holding = v.holdingPeriod as string;

      let taxRate = 0;
      let taxType = "";

      if (type === "equity") {
        taxRate = holding === "short" ? 0.15 : 0.10; // STCG 15%, LTCG 10%
        taxType = holding === "short" ? "STCG 15%" : "LTCG 10%";
      } else if (type === "debt") {
        taxRate = 0.30; // Taxable as income for NRI
        taxType = "Slab rate (30% for NRI)";
      } else {
        taxRate = holding === "short" ? 0.30 : 0.20; // Balanced treated as debt
        taxType = holding === "short" ? "Slab rate" : "20% LTCG";
      }

      const capitalGainsTax = Math.round(gain * taxRate);
      const cess = Math.round(capitalGainsTax * 0.04);
      const totalTax = capitalGainsTax + cess;
      const netGain = gain - totalTax;

      return { gain, capitalGainsTax, cess, totalTax, netGain, taxType };
    },
    resultFields: [
      { key: "totalTax", label: "Total Tax on Gains", format: "currency", isPrimary: true },
      { key: "netGain", label: "Net Gain After Tax", format: "currency" },
      { key: "capitalGainsTax", label: "Capital Gains Tax", format: "currency" },
      { key: "cess", label: "4% Cess", format: "currency" },
      { key: "taxType", label: "Tax Category", format: "text" },
    ],
    content: {
      whatIs: "NRI mutual fund taxation: Equity funds - STCG 15% (<1 year), LTCG 10% (>1 year). Debt funds - taxed as income at 30% + 4% cess (31.2% total for NRIs). Balanced funds - hybrid treatment. Long-term holding (>1 year) significantly reduces tax. NRIs must file ITR in India and may claim DTAA relief if also taxed in native country. Dividend distribution is taxable income (30% for NRIs).",
      faqs: [
        { question: "What's the capital gains tax on NRI mutual fund in India?", answer: "Equity STCG: 15%, LTCG: 10%. Debt: 30% (slab rate). Balanced: mixed. Plus 4% cess on all. Long-term holding (>1 year) saves significant tax." },
        { question: "Can NRI hold mutual funds in India?", answer: "Yes, fully allowed. Open account with any fund house. Dividend/gain taxable. No repatriation limit on mutual fund gains." },
        { question: "Should NRI invest in equity or debt mutual funds in India?", answer: "Equity for long-term growth (LTCG 10% tax) and inflation hedge. Debt for stability but higher tax (30%). Balance based on risk tolerance." },
        { question: "Is dividend income from mutual fund taxable for NRI in India?", answer: "Yes, dividend taxed at 30% + 4% cess = 31.2% for NRIs (no slab rate benefit). Consider growth funds for tax efficiency." },
        { question: "Can NRI claim DTAA relief on mutual fund gains in India?", answer: "Yes, if mutual fund gains taxed in native country too. File ITR in India and claim foreign tax credit under DTAA." },
      ],
      relatedSlugs: ["nri/nri-investment-comparison", "nri/dtaa-relief-calculator", "nri/nri-rental-income-tax"],
    },
  },
];
